import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';
import {
    getActiveSchool,
    setActiveSchool,
    isActiveSchoolValidInList
} from '../utils/activeSchool';
import { markFreelanceCoach, isPlatformAdmin } from '../utils/roles';

const ACCESS_TO_NAV_ROLE = { owner: 'Owner', coach: 'Coach', member: 'Member', staff: 'Staff', demo: 'Owner' };

function buildActiveSchoolEntry(s) {
    return {
        schoolId: s.schoolId,
        schoolName: s.schoolName,
        schoolLogo: s.schoolLogo,
        accessRole: s.accessRole,
        studentId: s.studentId ?? null,
        isDemoSchool: s.isDemoSchool || s.accessRole === 'demo',
        isFreelanceSchool: !!s.isFreelanceSchool
    };
}

function syncFromSchoolList(list) {
    if (list.some(s => s.isFreelanceSchool)) markFreelanceCoach();
}

function syncNavRoleFromSchool(school) {
    if (isPlatformAdmin()) return;
    const navRole = ACCESS_TO_NAV_ROLE[school.accessRole];
    if (navRole) localStorage.setItem('role', navRole);
    if (school.isFreelanceSchool) markFreelanceCoach();
}

const SKIP_PATHS = [
    '/login', '/register', '/demo-login', '/privacy-policy', '/privacy', '/terms', '/offer', '/cookies', '/refund',
    '/profile', '/select-school', '/select-profile', '/account-type',
    '/payment/school-success', '/billing/success', '/pricing',
];

export default function SchoolSessionGate({ children }) {
    const { t } = useTranslation('common');
    const location = useLocation();
    const navigate = useNavigate();
    const [ready, setReady] = useState(false);
    const token = localStorage.getItem('token');

    useEffect(() => {
        if (!token) {
            setReady(true);
            return;
        }

        const path = location.pathname;
        if (SKIP_PATHS.some(p => path.startsWith(p))) {
            setReady(true);
            return;
        }

        let cancelled = false;

        (async () => {
            if (isPlatformAdmin()) {
                setReady(true);
                return;
            }

            try {
                const api = axios.create({
                    baseURL: getApiBaseUrl(),
                    headers: { Authorization: `Bearer ${token}` }
                });
                const res = await api.get('/school/available');
                if (cancelled) return;

                const list = res.data || [];
                syncFromSchoolList(list);

                if (list.length === 0) {
                    setReady(true);
                    return;
                }

                if (list.length === 1) {
                    const s = list[0];
                    const cur = getActiveSchool();
                    if (!cur || cur.schoolId !== s.schoolId || cur.isFreelanceSchool !== !!s.isFreelanceSchool) {
                        setActiveSchool(buildActiveSchoolEntry(s));
                        syncNavRoleFromSchool(s);
                    } else if (s.isFreelanceSchool) {
                        markFreelanceCoach();
                    }
                    setReady(true);
                    return;
                }

                if (!isActiveSchoolValidInList(list)) {
                    navigate('/select-school', { replace: true, state: { from: path } });
                    return;
                }

                setReady(true);
            } catch {
                if (!cancelled) setReady(true);
            }
        })();

        return () => { cancelled = true; };
    }, [token, location.pathname, navigate]);

    if (!ready) {
        return (
            <div style={{
                minHeight: '40vh', display: 'flex', alignItems: 'center',
                justifyContent: 'center', color: '#888', fontFamily: "'Segoe UI', Arial, sans-serif"
            }}>
                {t('loading')}
            </div>
        );
    }

    return children;
}
